/**
 * crash-logger.js — lightweight production error logger + metrics.
 *
 * Pluggable: call setReporter(fn) to forward events to Sentry, a
 * Supabase `app_errors` table, LogRocket, etc.
 * Without a reporter, errors are silently discarded in production and
 * printed to the console in development.
 *
 * Usage:
 *   crashLogger.init();                        // call once at app start
 *   crashLogger.setReporter(fn);               // plug in Sentry / Supabase
 *   crashLogger.error('msg', extra);           // manual log
 *   crashLogger.crash(err, info);              // from ErrorBoundary
 *   crashLogger.metric('sync_duration', 1234); // performance metric (ms)
 *   const stop = crashLogger.startTimer('startup'); stop(); // auto-timing
 */

const isDev = process.env.NODE_ENV !== 'production';

let _reporter = null;
let _initialized = false;

function _emit(level, message, extra = {}) {
  if (isDev) {
    const method = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
    // eslint-disable-next-line no-console
    console[method](`[keves:${level}]`, message, extra);
  }
  try {
    _reporter?.(level, message, { ...extra, ts: Date.now() });
  } catch {
    // Reporter failures must never crash the app.
  }
}

export const crashLogger = {
  /** Plug in an external reporter: (level, message, extra) => void */
  setReporter(fn) {
    _reporter = fn;
  },

  error(message, extra) { _emit('error', message, extra); },
  warn(message, extra)  { _emit('warn',  message, extra); },
  info(message, extra)  { _emit('info',  message, extra); },

  /**
   * Emit a named performance metric.
   * @param {string} name  e.g. 'sync_duration', 'startup_ms', 'queue_size'
   * @param {number} value numeric measurement (ms for durations, count for sizes)
   * @param {object} [tags] optional key-value dimensions
   */
  metric(name, value, tags = {}) {
    _emit('info', 'metric', { metric: name, value, ...tags });
  },

  /**
   * Start a high-resolution timer. Returns a stop() function that
   * automatically emits a metric with the elapsed milliseconds.
   * @param {string} name  metric name
   * @param {object} [tags]
   * @returns {() => number} stop — call to end the timer and emit the metric
   */
  startTimer(name, tags = {}) {
    const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return () => {
      const ms = Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - start
      );
      this.metric(name, ms, tags);
      return ms;
    };
  },

  /** Called from ErrorBoundary.componentDidCatch */
  crash(error, componentInfo) {
    _emit('error', 'react_crash', {
      message:  error?.message,
      stack:    error?.stack?.slice(0, 2000),
      component: componentInfo?.componentStack?.split('\n')?.[1]?.trim(),
    });
  },

  /** Call once at app start. Safe to call multiple times. */
  init() {
    if (_initialized || typeof window === 'undefined') return;
    _initialized = true;

    window.addEventListener('error', (event) => {
      _emit('error', 'unhandled_error', {
        message:  event.message,
        filename: event.filename,
        lineno:   event.lineno,
        colno:    event.colno,
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      _emit('error', 'unhandled_rejection', {
        message: reason?.message ?? String(reason),
        stack:   reason?.stack?.slice(0, 2000),
      });
    });

    // Emit startup timing metric once the page is interactive
    if (typeof window.performance?.timing !== 'undefined') {
      const onLoad = () => {
        const t = window.performance.timing;
        if (t.loadEventEnd && t.navigationStart) {
          this.metric('startup_ms', t.loadEventEnd - t.navigationStart);
        }
      };
      if (document.readyState === 'complete') {
        onLoad();
      } else {
        window.addEventListener('load', onLoad, { once: true });
      }
    }
  },
};
